import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import SEO from "../components/SEO";
import InquiryForm from "../components/InquiryForm";
import { fetchContactInfo } from "../lib/api";

const DEFAULTS = {
  business_name: "Nexora Learning & Development Centre",
  phone: "604-655-5306",
  email: "admissions@nexoracentre.ca",
  address: "",
  hours: "Mon–Sat · 9am–6pm",
  contact_page_message:
    "Contact us to learn whether Play-Based Behaviour & Emotional Support is a good fit for your child. There's no pressure — a team member will review your message and reach out shortly.",
};

export default function ContactPage() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    fetchContactInfo()
      .then((d) => setInfo({ ...DEFAULTS, ...d }))
      .catch(() => setInfo(DEFAULTS));
  }, []);

  const v = info ?? DEFAULTS;
  const phoneDigits = (v.phone || "").replace(/\D/g, "");
  const blocks = [
    v.email && { icon: Mail, k: "Email", v: v.email, href: `mailto:${v.email}` },
    v.phone && {
      icon: Phone,
      k: "Phone",
      v: v.phone,
      href: `tel:+${phoneDigits.length === 10 ? `1${phoneDigits}` : phoneDigits}`,
    },
    v.address && { icon: MapPin, k: "Address", v: v.address },
    v.hours && { icon: Clock, k: "Hours", v: v.hours },
  ].filter(Boolean);

  return (
    <>
      <SEO
        path="/contact"
        title="Contact Nexora | Play-Based Behaviour & Emotional Support"
        description="Contact Nexora Learning & Development Centre about Play-Based Behaviour & Emotional Support for your child."
      />

      <section className="bg-[#FFFCF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-16">
          <p className="eyebrow">Contact</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-extrabold text-slate-900 mt-4 max-w-3xl leading-[1.06]">
            Get in touch — we reply within one business day.
          </h1>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
            {v.contact_page_message}
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="rounded-[2.5rem] bg-slate-900 text-white p-8 lg:p-10">
              <h3 className="font-display text-2xl font-extrabold tracking-tight">
                Reach us directly
              </h3>
              <div className="mt-8 grid gap-6" data-testid="contact-info-blocks">
                {blocks.map((b) => (
                  <div key={b.k} className="flex items-start gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 border border-white/15">
                      <b.icon className="h-5 w-5 text-white" />
                    </span>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        {b.k}
                      </p>
                      <p className="font-display font-bold text-white mt-1">
                        {b.href ? (
                          <a href={b.href} className="hover:underline" data-testid={`contact-${b.k.toLowerCase()}-link`}>
                            {b.v}
                          </a>
                        ) : (
                          b.v
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-10 text-xs text-slate-400 leading-relaxed">
                Nexora provides educational and developmental support only. We
                do not provide medical diagnosis or clinical treatment.
              </p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <InquiryForm
              source="contact_page"
              sourcePage="/contact"
              title="Send us a message"
              subtitle="Required fields are marked with *"
              ctaLabel="Request Information"
            />
          </div>
        </div>
      </section>
    </>
  );
}
